// ============================================================
// HoverChat Admin — Main App with Routing
// ============================================================

import { useState, useEffect, createContext, useContext } from 'react';
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { api } from './lib/api';
import { Sidebar } from './components/layout/Sidebar';
import { LoginPage } from './pages/auth/LoginPage';
import { MagicLinkVerifyPage } from './pages/auth/MagicLinkVerifyPage';
import { DashboardPage } from './pages/DashboardPage';
import { KnowledgeBasePage } from './pages/KnowledgeBasePage';
import { SystemPromptsPage } from './pages/SystemPromptsPage';
import { TenantsPage } from './pages/tenants/TenantsPage';
import { TenantDetailPage } from './pages/tenants/TenantDetailPage';
import { UsersPage } from './pages/users/UsersPage';

// Auth context
interface AuthContextType {
  user: any | null;
  login: (email: string, password: string) => Promise<void>;
  logout: () => Promise<void>;
  isLoading: boolean;
}

export const AuthContext = createContext<AuthContextType>({
  user: null,
  login: async () => {},
  logout: async () => {},
  isLoading: true,
});

export function useAuth() {
  return useContext(AuthContext);
}

function App() {
  const [user, setUser] = useState<any | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    // Check if user is logged in
    const token = localStorage.getItem('hoverchat_token');
    if (token) {
      api.setToken(token);
      api.getMe()
        .then(({ user }) => setUser(user))
        .catch(() => {
          localStorage.removeItem('hoverchat_token');
          setUser(null);
        })
        .finally(() => setIsLoading(false));
    } else {
      setIsLoading(false);
    }
  }, []);

  const login = async (email: string, password: string) => {
    const result = await api.login(email, password);
    setUser(result.user);
    navigate('/');
  };

  const logout = async () => {
    await api.logout();
    setUser(null);
    navigate('/login');
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <AuthContext.Provider value={{ user, login, logout, isLoading }}>
      {!user ? (
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/auth/magic-link/verify" element={<MagicLinkVerifyPage />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      ) : (
        <div className="flex h-screen bg-gray-50">
          <Sidebar />
          <main className="flex-1 overflow-auto">
            <Routes>
              <Route path="/" element={<DashboardPage />} />
              <Route path="/knowledge-base" element={<KnowledgeBasePage />} />
              <Route path="/system-prompts" element={<SystemPromptsPage />} />
              <Route path="/tenants" element={<TenantsPage />} />
              <Route path="/tenants/:id/*" element={<TenantDetailPage />} />
              <Route path="/users" element={<UsersPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </main>
        </div>
      )}
    </AuthContext.Provider>
  );
}

export default App;
